import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Camera, ArrowLeft, CheckCircle2, XCircle, Lock, Trash2 } from "lucide-react";

export default function LoteRetiro() {
  const { id } = useParams();
  const nav = useNavigate();
  const [batch, setBatch] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);
  const scannerDivId = "qr-scanner-div";

  const load = () => api.get(`/batches/${id}`).then((r) => setBatch(r.data));
  useEffect(() => { load(); }, [id]);

  const startScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode(scannerDivId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decoded) => {
            await handleScan(decoded);
          },
          () => {}
        );
      } catch (e) {
        toast.error("No se pudo acceder a la cámara: " + e.message);
        setScanning(false);
      }
    }, 100);
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); scannerRef.current.clear(); } catch (e) { void e; }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleScan = async (tracking_number) => {
    // Prevent duplicates by pausing briefly
    if (scannerRef.current) {
      try { await scannerRef.current.pause(true); } catch (e) { void e; }
    }
    try {
      const r = await api.post(`/batches/${id}/scan`, { tracking_number: tracking_number.trim() });
      if (r.data.bulto) {
        toast.success(`📦 BULTO ${r.data.code} · ${r.data.added} pedidos agregados${r.data.already ? ` (${r.data.already} ya escaneados)` : ""}`);
        if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
      } else if (r.data.already) {
        toast(`Ya escaneado: ${tracking_number}`);
      } else {
        toast.success(`✓ ${tracking_number}`);
        if (navigator.vibrate) navigator.vibrate(80);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTimeout(() => {
        if (scannerRef.current) { try { scannerRef.current.resume(); } catch (e) { void e; } }
      }, 900);
    }
  };

  const manualScan = async (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    await handleScan(manual.trim());
    setManual("");
  };

  const closeBatch = async () => {
    if (!confirm(`Cerrar lote con ${batch.order_ids.length} pedidos y marcarlos "en bodega"?`)) return;
    await stopScan();
    try {
      await api.post(`/batches/${id}/close`);
      toast.success("Lote cerrado — pedidos en bodega");
      nav("/repartidor");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelBatch = async () => {
    if (batch.order_ids.length > 0) {
      toast.error("El lote tiene pedidos escaneados. Cerralo o confirmá el retiro en su lugar.");
      return;
    }
    if (!confirm("¿Cancelar y eliminar este lote vacío?")) return;
    await stopScan();
    try {
      await api.delete(`/batches/${id}`);
      toast.success("Lote eliminado");
      nav("/repartidor");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmRetiro = async () => {
    if (!confirm(`Confirmar retiro de ${batch.order_ids.length} pedidos? Se notificará al aliado, destinatarios y admin.`)) return;
    await stopScan();
    try {
      const r = await api.post(`/batches/${id}/confirmar-retiro`);
      toast.success(`Retiro confirmado — ${r.data.confirmed} pedidos notificados`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => () => { stopScan(); }, []);

  if (!batch) return <div className="min-h-screen bg-zinc-950 text-zinc-500 flex items-center justify-center font-mono-tech">Cargando...</div>;
  const isOpen = batch.state === "abierto";

  return (
    <div className="min-h-screen bg-zinc-950 text-white bg-noise pb-32">
      <header className="border-b border-white/10 bg-zinc-950/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-md mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/repartidor" className="inline-flex items-center gap-2 text-zinc-400" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <span className={`font-mono-tech text-[10px] px-2 py-0.5 border uppercase tracking-widest ${isOpen ? "border-yellow-300/40 text-yellow-300" : "border-zinc-700 text-zinc-500"}`}>
            {batch.state}
          </span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">Lote de retiro</div>
          <div className="font-display font-black text-3xl tracking-tighter">{batch.cliente_name}</div>
          <div className="font-mono-tech text-xs text-zinc-500 mt-2">
            {batch.order_ids.length} escaneados · Abierto {new Date(batch.opened_at).toLocaleTimeString("es-CL")}
          </div>
        </div>

        {isOpen && (
          <>
            {!scanning ? (
              <button data-testid="start-scan" onClick={startScan} className="w-full bg-yellow-300 text-black font-bold p-6 uppercase tracking-widest flex items-center justify-between hover:bg-yellow-200 mb-4">
                <span className="flex items-center gap-3"><Camera className="w-6 h-6" /> Iniciar escaneo</span>
                <ScanLine className="w-6 h-6" />
              </button>
            ) : (
              <div className="mb-4">
                <div className="border-4 border-yellow-300 bg-black relative overflow-hidden" style={{ aspectRatio: "1" }}>
                  <div id={scannerDivId} className="w-full h-full"></div>
                </div>
                <button data-testid="stop-scan" onClick={stopScan} className="w-full mt-3 border border-white/20 text-white font-bold p-3 uppercase tracking-widest text-sm">
                  Detener escaneo
                </button>
              </div>
            )}

            <form onSubmit={manualScan} className="flex gap-2 mb-3">
              <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="VOY-... o BUL-..." className="flex-1 bg-transparent border border-white/20 focus:border-yellow-300 outline-none px-3 py-3 text-sm font-mono-tech" data-testid="manual-scan-input" />
              <button type="submit" className="border border-white/20 hover:border-yellow-300 px-4 py-3 uppercase tracking-widest text-xs" data-testid="manual-scan-btn">OK</button>
            </form>
            <div className="text-[10px] font-mono-tech text-zinc-500 uppercase tracking-widest mb-6 leading-relaxed">
              💡 Si el aliado te da un <b className="text-yellow-300">código BUL-</b> (bulto), al escanearlo se ingresan todos los pedidos del bulto en un solo paso.
            </div>

            <button data-testid="confirm-retiro" onClick={confirmRetiro} disabled={batch.order_ids.length === 0} className="w-full border-2 border-yellow-300 bg-yellow-300 text-black font-bold p-4 uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 mb-3 hover:brightness-95">
              ✓ Confirmar retiro ({batch.order_ids.length})
            </button>
            <button data-testid="close-batch" onClick={closeBatch} disabled={batch.order_ids.length === 0} className="w-full border-2 border-red-500/40 text-red-300 hover:bg-red-500/10 font-bold p-4 uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 mb-3">
              <Lock className="w-4 h-4" /> Cerrar → En bodega ({batch.order_ids.length})
            </button>
            {batch.order_ids.length === 0 && (
              <button data-testid="cancel-batch" onClick={cancelBatch} className="w-full border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500/40 font-bold p-3 uppercase tracking-widest text-xs flex items-center justify-center gap-2 mb-8">
                <Trash2 className="w-3.5 h-3.5" /> Cancelar y eliminar lote vacío
              </button>
            )}
            {batch.order_ids.length > 0 && <div className="mb-8" />}
          </>
        )}

        {/* Scanned orders */}
        <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest mb-3">/ Escaneados</div>
        <div className="space-y-2">
          {batch.orders?.map((o) => (
            <div key={o.id} className="border border-white/10 bg-zinc-900 p-4 flex items-start gap-3" data-testid={`scanned-${o.tracking_number}`}>
              <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono-tech text-sm truncate">{o.tracking_number}</div>
                <div className="text-xs text-zinc-400 truncate">{o.destinatario?.nombre} · {o.destinatario?.comuna}</div>
              </div>
            </div>
          ))}
          {batch.order_ids.length === 0 && <div className="text-zinc-600 text-sm font-mono-tech text-center py-6">Aún no hay escaneos</div>}
        </div>
      </main>
    </div>
  );
}
